import { Container } from '@tencent/alloy-renderer';
import { BackgroundManager } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-pc/background/background-manager';
import { MasterManager } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-pc/master-layout/master-manager';
import { LayoutManager } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-pc/master-layout/layout-manager';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { StableLayerService } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-pc/stable-layer/stable-layer-service';
import {
    IRenderProps,
    IStableLayerManagerProps,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-pc/stable-layer/stable-layer.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { IMasterModel } from '@src/slide/domain/ui-model/element-modeles/Interface/master-model.interface';
import { ILayoutModel } from '@src/slide/domain/ui-model/element-modeles/Interface/layout-model.interface';
import { GradientType } from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import { UIState } from '@src/slide/infrastructure/module/independent/ui-state';

const InvalidKey = ['shadeToTitle', 'colorType', 'colorValue', 'alpha', 'lumMod', 'lumOff', 'shade', 'id',
    'rotateWithShape', 'rotWithShape', 'syncLoad'];

/**
 * 稳定层：背景&母版&板式
 */
export class StableLayerManager {
    public backgroundManager: BackgroundManager;
    public masterManager: MasterManager;
    public layoutManager: LayoutManager;
    private stableLayerMap = new Map<string, StableLayerService>();
    private slideStableLayerMap = new Map<ISlideModel, StableLayerService>();
    private container: Container;
    private currentLayer?: StableLayerService;
    private props: IStableLayerManagerProps;

    public constructor(props: IStableLayerManagerProps) {
        this.props = props;
        this.container = new Container();
        // 构建背景、master、layout的manager
        this.initBackgroundManager();
        this.initMasterManager();
        this.initLayoutManager();
    }

    public updateWithData(scale: number): void {
        this.props.scale = scale;
        this.currentLayer?.updateInfo(scale);
        this.backgroundManager?.updateWithData(this.props.scale);
        this.masterManager?.updateWithData(this.props.scale);
        this.layoutManager?.updateWithData(this.props.scale);
    }

    /**
     * 更新 Stable Layer
     * @param slideModel
     */
    public updateStableLayer(slideModel?: ISlideModel): void {
        if (!slideModel) return;
        this.currentLayer?.hide();
        const props = {
            slideModel,
            masterModel: slideModel.type === UIModelType.MASTER ? undefined : slideModel.getRelationMasterModel(),
            layoutModel: slideModel.type === UIModelType.LAYOUT ? undefined : slideModel.getRelationLayoutModel(),
        };
        const key = this.generateKey(props);
        let stableLayerService = this.stableLayerMap.get(key);
        // 通过 slideModel 背景、masterModel 和 layoutModel 在缓存中查找 stableLayer，没有找到则创建新的 stableLayer
        if (!stableLayerService) {
            stableLayerService = this.buildStableLayerService(key);
            stableLayerService.isDirty = true;
        }

        const lastStableLayer = this.slideStableLayerMap.get(slideModel);
        // slideModel 上一次使用的 stableLayer 和 当前 stableLayer 不同时，
        // 将 slideModel 从上一次的 stableLayer 中移除，并更新 SlideModel 和 stableLayer 的映射
        if (lastStableLayer && lastStableLayer !== stableLayerService) {
            lastStableLayer.removeSlideModel(slideModel);
            this.slideStableLayerMap.set(slideModel, stableLayerService);
        }

        this.currentLayer = stableLayerService;
        this.currentLayer?.updateInfo(this.props.scale);
        this.currentLayer.addSlideModel(slideModel);

        this.masterManager?.updateMaster(slideModel?.getRelationMasterModel());
        this.layoutManager?.updateLayout(slideModel?.getRelationLayoutModel());
        this.backgroundManager?.updateBackground();

        if (stableLayerService.isDirty) {
            stableLayerService.render(props);
        }

        this.updateTranslate();
        this.currentLayer.show();
    }

    /**
     * 根据 slideModel、masterModel和layoutModel 构建 stableLayer
     * @param props
     */
    public buildStableLayer(props: IRenderProps): void {
        const { slideModel, masterModel, layoutModel } = props;

        this.masterManager.buildMasterByCache(masterModel);
        this.layoutManager.buildLayoutByCache(layoutModel);
        this.backgroundManager.renderBackgroundByCache(slideModel);

        const key = this.generateKey(props);
        if (this.stableLayerMap.has(key)) {
            const stableLayerService = this.stableLayerMap.get(key);
            this.slideStableLayerMap.set(slideModel, stableLayerService as StableLayerService);
            (stableLayerService as StableLayerService).addSlideModel(slideModel);
            return;
        }

        const stableLayerService = this.buildStableLayerService(key);
        this.slideStableLayerMap.set(slideModel, stableLayerService);
        stableLayerService.addSlideModel(slideModel);
        stableLayerService.render(props);
    }

    /**
     * 背景、master 或 layout 渲染树变更时回调
     * @param props
     */
    public handleStableLayerUpdate = (props: Partial<IRenderProps>): void => {
        if (UIState.isExportingElementImage) {
            // 单图片导出时会设置元素x、y进而触发此逻辑，但此更新是不必要的，且非当前页的更新更是会兜底成当前页，从而出错
            return;
        }
        const value = this.getValueFromProps(props);
        this.updateStableLayerProps(value);
        if (!value.slideModel) return;

        const key = this.generateKey(value as IRenderProps);
        const stableLayerService = this.stableLayerMap.get(key);
        const slideModel = value.slideModel as ISlideModel;
        const lastStableLayer = this.slideStableLayerMap.get(slideModel);
        const { currentSlide } = this.props.services.selectionService;

        if (!stableLayerService) {
            // 如果 stableLayer 不存在，并且是当前页渲染树变更，需要走重建 stableLayer 逻辑
            if (value.slideModel === currentSlide) {
                this.updateStableLayer(value.slideModel);
            }
            return;
        }

        // 当前页渲染树变更，并且 lastStableLayer 和当前 stableLayerService 不相等时，需要更新 currentLayer
        if (value.slideModel === currentSlide && lastStableLayer !== stableLayerService) {
            this.currentLayer?.hide();
            lastStableLayer?.removeSlideModel(slideModel);
            this.slideStableLayerMap.set(slideModel, stableLayerService);
            this.currentLayer = stableLayerService;
            this.currentLayer.addSlideModel(slideModel);
            this.currentLayer.show();
        }
        stableLayerService.render(value as IRenderProps);
        !UIState.isExportingToImage && this.updateTranslate();
    };

    public cleanCache(slideModel?: ISlideModel): void {
        if (!slideModel) return;
        this.backgroundManager.cleanBackground(slideModel);
        const stableLayer = this.slideStableLayerMap.get(slideModel);
        stableLayer?.removeSlideModel(slideModel);
        this.slideStableLayerMap.delete(slideModel);
    }

    public reloadMasterComponent(slideModel: ISlideModel): void {
        const masterModel = slideModel.getRelationMasterModel();
        this.masterManager.getMasterFromCache(masterModel)?.reloadComponent(masterModel);
        const props = {
            slideModel,
            masterModel,
            layoutModel: slideModel.type === UIModelType.LAYOUT ? undefined : slideModel.getRelationLayoutModel(),
        };
        const key = this.generateKey(props);
        const stableLayerService = this.stableLayerMap.get(key);
        if (!stableLayerService) {
            return;
        }
        stableLayerService.render(props);
    }

    public getStableLayerService(slideModel: ISlideModel): StableLayerService | undefined {
        return this.slideStableLayerMap.get(slideModel);
    }

    public updateTranslate() {
        const canvasSize = this.props.services.editAreaCanvasSizeManager?.getCanvasSize();
        const slideLeft = canvasSize?.slideLeft ?? 0;
        const slideTop = canvasSize?.slideTop ?? 0;
        const translateX = slideLeft - ((this.currentLayer?.container?.x || 0) * this.props.scale);
        const translateY = slideTop - ((this.currentLayer?.container?.y || 0) * this.props.scale);
        this.props.updateStableLayerTranslate(translateX, translateY);
    }

    private updateStableLayerProps(props: Partial<IRenderProps>) {
        if (props.slideModel) {
            props.masterModel = props.slideModel.type === UIModelType.MASTER
                ? undefined : props.slideModel.getRelationMasterModel();
            props.layoutModel = props.slideModel.type === UIModelType.LAYOUT
                ? undefined : props.slideModel.getRelationLayoutModel();
            if (props.slideModel.type === UIModelType.MASTER) {
                this.updateFlagWhenMasterChange(props.slideModel as IMasterModel);
            } else if (props.slideModel.type === UIModelType.LAYOUT) {
                this.updateFlagWhenLayoutChange(props.slideModel as ILayoutModel);
            }
        } else {
            this.handleMasterChange(props);
            this.handleLayoutChange(props);
        }
    }

    /**
     * master 渲染树变更时，更新其他页 stableLayer 状态，并重新渲染当前页
     * @param props
     * @private
     */
    private handleMasterChange(props: Partial<IRenderProps>) {
        if (!props.masterModel) {
            return;
        }
        this.updateFlagWhenMasterChange(props.masterModel);
        const { currentSlide } = this.props.services.selectionService;
        if (currentSlide?.getRelationMasterModel() === props.masterModel) {
            props.slideModel = currentSlide;
            props.layoutModel = currentSlide.getRelationLayoutModel();
        }
    }

    /**
     * layout 渲染树变更时，更新其他页 stableLayer 状态，并重新渲染当前页
     * @param props
     * @private
     */
    private handleLayoutChange(props: Partial<IRenderProps>) {
        if (!props.layoutModel) {
            return;
        }
        this.updateFlagWhenLayoutChange(props.layoutModel);
        const { currentSlide } = this.props.services.selectionService;
        if (currentSlide?.getRelationLayoutModel() === props.layoutModel) {
            props.slideModel = currentSlide;
            props.masterModel = currentSlide.getRelationMasterModel();
        }
    }

    /**
     * master 渲染树变更时，将使用该 master 的 slide 和 layout 对应的 stableLayer 置为 dirty
     * @param masterModel
     * @private
     */
    private updateFlagWhenMasterChange(masterModel: IMasterModel) {
        const slides = this.props.services.uiDataService.presentation.children
            .filter(slide => slide.getRelationMasterModel() === masterModel);
        slides.forEach((slide) => {
            const updateProps = {
                slideModel: slide,
                masterModel,
                layoutModel: slide.getRelationLayoutModel(),
            };
            const key = this.generateKey(updateProps as IRenderProps);
            const stableLayerService = this.stableLayerMap.get(key);
            if (stableLayerService) {
                stableLayerService.isDirty = true;
            }
        });
        const layouts = this.props.services.uiDataService.presentation.layoutList
            .filter(layout => layout.getRelationMasterModel() === masterModel);
        layouts.forEach((layout) => {
            const updateProps = {
                slideModel: layout,
                masterModel,
                layoutModel: undefined,
            };
            const key = this.generateKey(updateProps as Partial<IRenderProps>);
            const stableLayerService = this.stableLayerMap.get(key);
            if (stableLayerService) {
                stableLayerService.isDirty = true;
            }
        });
    }

    /**
     * layout 渲染树变更时，将使用该 layout 的 slide 对应的 stableLayer 置为 dirty
     * @param layoutModel
     * @private
     */
    private updateFlagWhenLayoutChange(layoutModel: ILayoutModel) {
        const slides = this.props.services.uiDataService.presentation.children
            .filter(slide => slide.getRelationLayoutModel() === layoutModel);
        slides.forEach((slide) => {
            const updateProps = {
                slideModel: slide,
                masterModel: slide.getRelationMasterModel(),
                layoutModel,
            };
            const key = this.generateKey(updateProps as IRenderProps);
            const stableLayerService = this.stableLayerMap.get(key);
            if (stableLayerService) {
                stableLayerService.isDirty = true;
            }
        });
    }

    private removeStableLayer = (stableLayer: StableLayerService) => {
        // eslint-disable-next-line no-restricted-syntax
        for (const key of this.stableLayerMap.keys()) {
            if (this.stableLayerMap.get(key) === stableLayer) {
                stableLayer.dispose();
                this.stableLayerMap.delete(key);
                return;
            }
        }
    };

    private buildStableLayerService(key: string): StableLayerService {
        const stableLayerService = new StableLayerService({
            stableLayerWrapper: this.props.elementWrapper,
            backgroundManager: this.backgroundManager,
            masterManager: this.masterManager,
            layoutManager: this.layoutManager,
            scale: this.props.scale,
            removeStableLayer: this.removeStableLayer,
        });
        this.stableLayerMap.set(key, stableLayerService);
        return stableLayerService;
    }

    /**
     * 根据背景、母版和板式生成 stableLayer 缓存 key
     * @param props
     * @private
     */
    private generateKey(props: Partial<IRenderProps>): string {
        const { slideModel, masterModel, layoutModel } = props;
        const masterId = this.masterManager.getMasterId(masterModel);
        const layoutId = this.layoutManager.getLayoutId(layoutModel);
        const fillInfo = slideModel?.getBackgroundFill().getFillInfo();
        let backgroundKey = '';
        try {
            // 将背景属性生成字符串作为缓存 key，过滤掉其中无意义的字段
            backgroundKey = JSON.stringify(fillInfo, (key, val) => {
                if (InvalidKey.indexOf(key) !== -1) {
                    return undefined;
                }
                return val;
            });
            const centerFrame = fillInfo?.gradFill?.pathGradient?.path === GradientType.Shape
                ? slideModel?.getBackgroundFill().getShadeTitleFrame() : undefined;
            backgroundKey += JSON.stringify(centerFrame);
        } catch (e) {
            backgroundKey = slideModel?.key as string;
            window.log.error(`[stable-layer]generate key error: ${e}`);
        }

        return `${masterId}_${layoutId}_${backgroundKey}_${(slideModel?.showMasterSp || 0)}`;
    }

    /**
     * 构建背景缓存manager
     */
    private initBackgroundManager() {
        this.backgroundManager = new BackgroundManager(
            this.props.scale,
            this.handleStableLayerUpdate,
            this.props.getApplication,
        );
    }

    /**
     * 构建master缓存manager
     */
    private initMasterManager() {
        this.masterManager = new MasterManager(
            this.props.scale,
            this.props.getApplication,
            this.handleStableLayerUpdate,
        );
    }

    /**
     * 构建layout缓存manager
     */
    private initLayoutManager() {
        this.layoutManager = new LayoutManager(
            this.props.scale,
            this.props.getApplication,
            this.handleStableLayerUpdate,
        );
    }

    private getValueFromProps(props: Partial<IRenderProps>): Partial<IRenderProps> {
        if (!props.fromInheritLayer) {
            return props;
        }
        const isMasterEdit = this.props.services.masterLayoutEditService?.isMasterEditType;
        const slideModel = isMasterEdit ? props.slideModel : undefined;
        const masterModel = (props.slideModel?.type === UIModelType.MASTER && !isMasterEdit)
            ? props.slideModel as IMasterModel : undefined;
        const layoutModel = (props.slideModel?.type === UIModelType.LAYOUT && !isMasterEdit)
            ? props.slideModel as ILayoutModel : undefined;
        return {
            slideModel,
            masterModel,
            layoutModel,
        };
    }
}
